/* eslint-disable @typescript-eslint/no-unused-vars */
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import React, { useEffect, useRef } from "react";
import { CarouselProps } from "react-material-ui-carousel/dist/components/types";
import { useGoogleAnalyticsContext, useWebStoreContext } from "contexts";
import { Swiper, SwiperRef, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import type { SwiperOptions } from "swiper/types";
import { CustomSwiperContainer } from "./styles";

import { setSelectedDealIndex } from "redux/slices";
import { isValidElement } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "redux/store";
import { usePopupsState } from "hooks/usePopupsState";
import { getTimeLeft } from "utils/time";
import { isTouchInteraction } from "utils/isTouchInteraction";
import { SectionSource } from "interfaces/web-store/section-source";

const swiperParams: SwiperOptions = {
	autoplay: {
		enabled: true,
		delay: 7000,
		pauseOnMouseEnter: true,
		disableOnInteraction: false
	},
	modules: [Autoplay, Navigation, Pagination],
	navigation: true,
	pagination: {
		clickable: true,
		dynamicBullets: true,
		dynamicMainBullets: 4
	},
	//loop: false,
	rewind: true,
	slidesPerView: 1,
	spaceBetween: 30,
	slidesOffsetAfter: 0
};
const CarouselComponentV2 = (
	props: CarouselProps & { from?: SectionSource }
) => {
	const { nowTime } = useWebStoreContext();
	const swiperRef = useRef<SwiperRef | null>(null);
	const { reportEvent, reportPlaygamiEvent } = useGoogleAnalyticsContext();
	const { isAnyPopupOpen } = usePopupsState();
	const dispatch = useDispatch();
	const { selectedDealIndex } = useSelector(
		(state: RootState) => state.featuredDeals
	);
	const isElite = props.from === "elite";
	const eventSource = isElite ? "vip_shop" : "feature_deal";
	const slides = Array.isArray(props.children) ? props.children : [];
	const slidesSignature = slides
		.map((slide) => slide?.props?.data?.id ?? "")
		.join("|");

	const reportRenderSlide = (n: number) => {
		if (!slides[n]) {
			return;
		}

		reportEvent("offer_rendered", {
			source: eventSource,
			offer_id: slides[n].props.data.id,
			reward_id: slides[n].props.data.rewardId,
			price: slides[n].props.data.price,
			loyalty_points: slides[n].props.data.loyaltyPointReward,
			offer_ends_time: slides[n].props.data.endTime,
			bannertext: slides[n].props.data.bannerText,
			image_url: slides[n].props.data.backgroundImageUrl
		});
		const timeLeft = getTimeLeft(
			nowTime,
			slides[n].props.data.endTime,
			"seconds"
		);
		reportPlaygamiEvent("tp.web_frontend", {
			event_name: "offer_rendered",
			event_type: "feature_deal",
			action: "offer_rendered",
			section_name: "feature_deal",
			source: eventSource,
			offer_id: slides?.[n]?.props?.data?.id,
			time_left: timeLeft,
			tag_text: slides?.[n]?.props?.data?.bannerText,
			reward_id: slides?.[n]?.props?.data?.rewardId,
			link_url: slides?.[n]?.props?.data?.backgroundImageUrl,
			price_in_cents: Math.round((slides?.[n]?.props?.data?.price ?? 0) * 100),
			value_int: slides?.[n]?.props?.data?.loyaltyPointReward
		});
	};

	const setAutoplay = (enabled: boolean) => {
		const swiper = swiperRef.current?.swiper;

		if (!swiper || swiper.destroyed) {
			return;
		}

		if (enabled) {
			if (!swiper.autoplay.running) {
				swiper.autoplay.paused = false;
				swiper.autoplay.start();
			}
			return;
		}

		if (swiper.autoplay.running) {
			swiper.autoplay.stop();
		}
		swiper.autoplay.paused = false;
	};

	useEffect(() => {
		setAutoplay(!isAnyPopupOpen);
	}, [isAnyPopupOpen, props?.children]);

	useEffect(() => {
		if (selectedDealIndex !== null && swiperRef.current?.swiper) {
			swiperRef.current.swiper.slideToLoop(selectedDealIndex);
			setTimeout(() => {
				document
					.getElementById("carousel-swiper")
					?.scrollIntoView({ behavior: "smooth", block: "center" });
			}, 200);

			setTimeout(() => {
				dispatch(setSelectedDealIndex(null));
			}, 1000);
		}
	}, [selectedDealIndex, swiperRef]);

	useEffect(() => {
		if (!slides.length) {
			return;
		}
		reportRenderSlide(0);
	}, [slidesSignature]);

	return (
		<CustomSwiperContainer>
			<Swiper
				id="carousel-swiper"
				ref={swiperRef}
				{...swiperParams}
				onTouchStart={(_, event) => {
					if (isTouchInteraction(event)) {
						setAutoplay(false);
					}
				}}
				onTouchEnd={(_, event) => {
					if (isTouchInteraction(event)) {
						setAutoplay(!isAnyPopupOpen);
					}
				}}
				onSlideChangeTransitionStart={(swiper) => {
					reportRenderSlide(swiper.realIndex);
				}}
			>
				{React.Children.map(props.children, (child, i) =>
					isValidElement(child) ? (
						<SwiperSlide key={`${child.key} ${i}`}>{child}</SwiperSlide>
					) : null
				)}
			</Swiper>
		</CustomSwiperContainer>
	);
};

export default CarouselComponentV2;
